// Derived metrics for the live game-day view. Pure functions over the data the controller already loads.
import moment from 'moment-timezone';

const MINUTE = 60 * 1000;

// The dashboard always shows venue time. Readings are venue-local clock strings
// ("YYYY-MM-DDTHH:mm:ss", no zone), read as local clock time like the schedule's "HH:mm" times.
// A time that carries a zone (UTC "Z" or "+hh:mm") is converted to the venue's clock first,
// so the charts never show the viewer's own time zone.
export const toVenueTime = (time, timeZone) => {
  if (typeof time !== 'string' || !/(Z|[+-]\d{2}:?\d{2})$/.test(time)) return time;
  return moment(time).tz(timeZone || 'America/New_York').format('YYYY-MM-DDTHH:mm:ss');
};

// "Now" on the venue's clock, in the same local-clock form as the readings and the schedule
export const venueNowMs = (timeZone) =>
  new Date(new Date().toLocaleString('en-US', { timeZone: timeZone || 'America/New_York' })).getTime();

const toSeries = (sectionData) =>
  (sectionData?.data || [])
    .map((point) => ({
      t: new Date(point.x).getTime(),
      y: typeof point.y === 'number' ? point.y : 0,
      o: point.overflow ? 1 : 0, // overflow: queue was at least y
    }))
    .filter((point) => !Number.isNaN(point.t))
    .sort((a, b) => a.t - b.t);

// { [sectionId]: [{ t, y, o }] }
export const seriesBySection = (datasets = []) => {
  const result = {};
  datasets.forEach((sectionData) => {
    result[sectionData.section] = toSeries(sectionData);
  });
  return result;
};

// Average across sections per minute. `o` is set when any section overflowed that minute.
export const averageSeries = (bySection) => {
  const buckets = {};
  Object.values(bySection).forEach((series) => {
    series.forEach(({ t, y, o }) => {
      const key = Math.floor(t / MINUTE) * MINUTE;
      if (!buckets[key]) buckets[key] = { values: [], o: 0 };
      buckets[key].values.push(y);
      if (o) buckets[key].o = 1;
    });
  });
  return Object.keys(buckets)
    .map(Number)
    .sort((a, b) => a - b)
    .map((t) => ({
      t,
      y: buckets[t].values.reduce((sum, v) => sum + v, 0) / buckets[t].values.length,
      o: buckets[t].o,
    }));
};

export const lastPoint = (series) => (series && series.length ? series[series.length - 1] : null);

// Last value at or before `t`
const valueAt = (series, t) => {
  let found = null;
  for (let i = 0; i < series.length; i++) {
    if (series[i].t <= t) found = series[i];
    else break;
  }
  return found;
};

// Change between the latest value and the value `minutes` earlier
export const trend = (series, minutes = 15) => {
  const latest = lastPoint(series);
  if (!latest) return null;
  const earlier = valueAt(series, latest.t - minutes * MINUTE);
  if (!earlier || earlier === latest) return null;
  return latest.y - earlier.y;
};

// Points in the last `minutes` before the latest point
export const recentWindow = (series, minutes = 60) => {
  const latest = lastPoint(series);
  if (!latest) return [];
  return series.filter((point) => point.t >= latest.t - minutes * MINUTE);
};

// Duration of a point, capped so data gaps don't inflate totals
const pointMinutes = (series, i) => {
  const next = series[i + 1];
  return next ? Math.min((next.t - series[i].t) / MINUTE, 5) : 1;
};

// Minutes where the value was above `threshold` (overflow always counts as over)
// `atOrAbove`: queues count a minute once the rounded value reaches the threshold (10 min is over);
// people counts keep "above the threshold"
export const minutesOver = (series, threshold, atOrAbove = false) => {
  if (!series.length) return { minutes: 0, share: 0, total: 0 };
  const over = (y) => (atOrAbove ? Math.round(y) >= threshold : y > threshold);
  let minutes = 0;
  for (let i = 0; i < series.length; i++) {
    if (!over(series[i].y) && !series[i].o) continue;
    minutes += pointMinutes(series, i);
  }
  const total = Math.max(1, (lastPoint(series).t - series[0].t) / MINUTE + 1);
  return { minutes: Math.round(minutes), share: Math.min(1, minutes / total), total: Math.round(total) };
};

// Overflow time and number of separate overflow episodes.
// Readings within EPISODE_GAP of each other count as one episode, so brief flickers don't inflate the count.
const EPISODE_GAP = 3 * MINUTE;

export const overflowSummary = (series) => {
  let minutes = 0;
  let episodes = 0;
  let lastOverflowAt = null;
  for (let i = 0; i < series.length; i++) {
    if (!series[i].o) continue;
    minutes += pointMinutes(series, i);
    if (lastOverflowAt === null || series[i].t - lastOverflowAt > EPISODE_GAP) episodes += 1;
    lastOverflowAt = series[i].t;
  }
  return { minutes: Math.round(minutes), episodes };
};

// Stand-level overflow from the raw section readings: time when any section was overflowing.
// Each overflowing reading covers the same span overflowSummary gives it, spans are merged across
// sections, and stretches split the same way episodes do, so a one-section stand matches its tile.
// Each stretch: { start, end, minutes, ongoing } (ongoing: a section's latest reading is in it).
export const overflowAcrossSections = (bySection) => {
  const spans = [];
  Object.values(bySection).forEach((series) => {
    if (!series) return;
    for (let i = 0; i < series.length; i++) {
      if (!series[i].o) continue;
      spans.push({ start: series[i].t, end: series[i].t + pointMinutes(series, i) * MINUTE, last: i === series.length - 1 });
    }
  });
  spans.sort((a, b) => a.start - b.start);
  const stretches = [];
  let lastOverflowAt = null;
  spans.forEach((span) => {
    let stretch = stretches[stretches.length - 1];
    if (!stretch || (span.start > stretch.end && span.start - lastOverflowAt > EPISODE_GAP)) {
      stretch = { start: span.start, end: span.start, covered: 0, ongoing: false };
      stretches.push(stretch);
    }
    // Only count the part of this span not already covered by another section
    stretch.covered += Math.max(0, span.end - Math.max(span.start, stretch.end));
    stretch.end = Math.max(stretch.end, span.end);
    stretch.ongoing = stretch.ongoing || span.last;
    lastOverflowAt = span.start;
  });
  const covered = stretches.reduce((sum, s) => sum + s.covered, 0) / MINUTE;
  return {
    minutes: Math.round(covered),
    episodes: stretches.length,
    stretches: stretches.map(({ covered: c, ...s }) => ({ ...s, minutes: Math.max(1, Math.round(c / MINUTE)) })),
  };
};

// Highest value in a series and whether that reading overflowed
export const peakOf = (series) => {
  let peak = null;
  series.forEach((point) => {
    if (!peak || point.y > peak.y || (point.y === peak.y && point.o && !peak.o)) peak = point;
  });
  return peak;
};

export const latestTimestamp = (bySection) => {
  let latest = null;
  Object.values(bySection).forEach((series) => {
    const point = lastPoint(series);
    if (point && (!latest || point.t > latest)) latest = point.t;
  });
  return latest;
};

export const formatClock = (t) =>
  t ? new Date(t).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '--';

export const formatDuration = (minutes) => {
  const total = Math.max(0, Math.round(minutes));
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m}m`;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
};

// Game phase from the schedule's "HH:mm" strings (same local-time parsing the charts use)
export const gamePhase = (currentGame, nowMs = Date.now()) => {
  if (!currentGame?.date || !currentGame?.gameStartTime) return null;
  const start = new Date(`${currentGame.date}T${currentGame.gameStartTime}:00`).getTime();
  const gates = currentGame.gatesOpen ? new Date(`${currentGame.date}T${currentGame.gatesOpen}:00`).getTime() : null;
  if (Number.isNaN(start)) return null;

  const sinceStart = (nowMs - start) / MINUTE;
  if (sinceStart >= 0) {
    return { label: 'In game', detail: `${formatDuration(sinceStart)} since game start`, start, gates };
  }
  if (gates && nowMs >= gates) {
    return { label: 'Gates open', detail: `Game starts in ${formatDuration(-sinceStart)}`, start, gates };
  }
  // Pregame is always before gates open
  return { label: 'Pregame', detail: `Game starts in ${formatDuration(-sinceStart)}`, start, gates };
};

// Average of a series in fixed time buckets (e.g. every 30 minutes)
export const bucketize = (series, bucketMinutes = 30) => {
  if (!series.length) return [];
  const size = bucketMinutes * MINUTE;
  const buckets = {};
  series.forEach(({ t, y, o }) => {
    const start = Math.floor(t / size) * size;
    if (!buckets[start]) buckets[start] = { start, end: start + size, total: 0, count: 0, o: 0 };
    buckets[start].total += y;
    buckets[start].count += 1;
    if (o) buckets[start].o = 1;
  });
  return Object.values(buckets)
    .sort((a, b) => a.start - b.start)
    .map((b) => ({ start: b.start, end: b.end, value: b.total / b.count, overflow: b.o }));
};

// Start of the schedule's game time as a timestamp
export const gameTimes = (currentGame) => {
  if (!currentGame?.date) return { start: null, gates: null };
  const parse = (hhmm) => {
    if (!hhmm) return null;
    const t = new Date(`${currentGame.date}T${hhmm}:00`).getTime();
    return Number.isNaN(t) ? null : t;
  };
  return { start: parse(currentGame.gameStartTime), gates: parse(currentGame.gatesOpen) };
};

// Per-minute gap between the longest and shortest section
export const deltaSeries = (bySection) => {
  const buckets = {};
  Object.values(bySection).forEach((series) => {
    series.forEach(({ t, y }) => {
      const key = Math.floor(t / MINUTE) * MINUTE;
      if (!buckets[key]) buckets[key] = { min: y, max: y, n: 0 };
      buckets[key].min = Math.min(buckets[key].min, y);
      buckets[key].max = Math.max(buckets[key].max, y);
      buckets[key].n += 1;
    });
  });
  return Object.keys(buckets)
    .map(Number)
    .sort((a, b) => a - b)
    .filter((t) => buckets[t].n > 1)
    .map((t) => ({ t, y: buckets[t].max - buckets[t].min, o: 0 }));
};
